import { getLocaleFirstDayOfWeek } from '@angular/common';
import { BACKEND_DATE_FORMAT } from '@app/app.constants';
import { AppInjectorService } from '@app/core/services/app-injector.service';
import { DateRange } from '@app/shared/models/date-range';
import { RawDate } from '@app/shared/models/raw-date';
import { portalError } from '@app/utils/error/portal-error';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  endOfDay,
  isAfter,
  isBefore,
  isMatch,
  lastDayOfMonth,
  lastDayOfQuarter,
  setDate,
  setDay,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
} from 'date-fns';
import { de as datefnsDe, enUS as datefnsEn } from 'date-fns/locale';
import moment, { unitOfTime } from 'moment';

export { moment };

export class DateUtils {
  /**
   * Calculate the absolute distance between the two given dates
   * @param raw1
   * @param raw2
   * @param unit
   */
  static absoluteDifference(raw1: RawDate, raw2: RawDate, unit: unitOfTime.Diff): number {
    return Math.abs(DateUtils.difference(raw1, raw2, unit));
  }

  static addDays(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return addDays(date, amount);
  }

  static addMonths(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return addMonths(date, amount);
  }

  static addQuarters(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return addQuarters(date, amount);
  }

  static addWeeks(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return addWeeks(date, amount);
  }

  static backend(raw: RawDate): string {
    return moment(this.parse(raw)).format(BACKEND_DATE_FORMAT);
  }

  /**
   * Calculate the difference between the two given dates
   *
   * @param raw1
   * @param raw2
   * @param unit
   */
  static difference(raw1: RawDate, raw2: RawDate, unit: unitOfTime.Diff): number {
    const date1 = moment(raw1);
    const date2 = moment(raw2);

    return date1.diff(date2, unit);
  }

  static endOfDay(raw: RawDate) {
    return endOfDay(DateUtils.parse(raw));
  }

  static endOfToday() {
    return DateUtils.endOfDay(DateUtils.today());
  }

  static equal(raw1: RawDate, raw2: RawDate): boolean {
    const date1 = moment(raw1);
    const date2 = moment(raw2);

    return date1.isSame(date2);
  }

  static firstDayOfMonth(raw: RawDate) {
    const date = DateUtils.parse(raw);

    return startOfMonth(date);
  }

  static firstDayOfQuarter(raw: RawDate) {
    const date = DateUtils.parse(raw);

    return startOfQuarter(date);
  }

  /**
   * Get a date object representing the first day of the week of the given date
   * @param raw Raw date object
   */
  static firstDayOfWeek(raw: RawDate) {
    const date = DateUtils.parse(raw);

    const locale = AppInjectorService.injector.get(TranslateService).currentLang;

    return setDay(date, getLocaleFirstDayOfWeek(locale));
  }

  static format(raw: RawDate, destinationFormat: string, rawFormat?: string): string {
    return moment(raw, rawFormat).format(destinationFormat);
  }

  static fromIso(date: RawDate): string {
    return DateUtils.format(date, 'HH:mm', 'HH:mm:ss');
  }

  static getDateFnsLocale(language: string) {
    if (language === 'de') {
      return datefnsDe;
    } else if (language === 'en') {
      return datefnsEn;
    }

    throw portalError({
      cause: new Error(`Language "${language}" is not supported`),
      id: 'date:unsupported-locale',
    });
  }

  static inRange(raw: RawDate, range: DateRange) {
    const date = DateUtils.parse(raw);

    return this.overlaps({ from: date, to: date }, range);
  }

  static isAfter(raw1: RawDate, raw2: RawDate) {
    const date1 = DateUtils.parse(raw1);
    const date2 = DateUtils.parse(raw2);

    return isAfter(date1, date2);
  }

  static isBefore(raw1: RawDate, raw2: RawDate) {
    const date1 = DateUtils.parse(raw1);
    const date2 = DateUtils.parse(raw2);

    return isBefore(date1, date2);
  }

  static getTime(raw: string): [number, number] | null {
    const matches = /^(\d\d):?(\d\d)$/.exec(raw);

    if (matches != null && matches.length === 3) {
      const [hours, minutes] = [Number(matches[1]), Number(matches[2])];

      if (Number.isNaN(hours)) {
        return null;
      } else if (Number.isNaN(minutes)) {
        return null;
      }

      return [hours, minutes];
    }

    return null;
  }

  static isValidTime(raw: string): boolean {
    return /^\d\d:?\d\d$/.test(raw) && (isMatch(raw, 'HH:mm') || isMatch(raw, 'HHmm'));
  }

  static lastDayOfMonth(raw: RawDate) {
    const date = DateUtils.parse(raw);

    return lastDayOfMonth(date);
  }

  static lastDayOfQuarter(raw: RawDate) {
    const date = DateUtils.parse(raw);

    return lastDayOfQuarter(date);
  }

  static lastDayOfWeek(raw: RawDate) {
    const firstDay = DateUtils.firstDayOfWeek(raw);

    return setDate(firstDay, firstDay.getDate() + 6);
  }

  static parse(raw: RawDate, format?: string): Date {
    let date;

    if (raw instanceof Date) {
      date = raw;
    } else if (
      typeof raw === 'object' &&
      'year' in raw &&
      'month' in raw &&
      'day' in raw &&
      typeof raw.year === 'number' &&
      typeof raw.month === 'number' &&
      typeof raw.day === 'number'
    ) {
      // Duck-typing because we don't get NgbDate instances from angular reactive forms
      date = new Date(raw.year, raw.month - 1, raw.day);
    } else if (raw === 'today') {
      date = DateUtils.startOfToday();
    } else if (raw !== undefined) {
      date = moment(raw, format).toDate();
    }

    return date;
  }

  static parseToNgbDate(raw: RawDate): NgbDate {
    const date = this.parse(raw);

    return NgbDate.from({
      // 'date' returns the date of the month ('day' means the day of the week)
      day: date.getDate(),
      // Month is zero based whilst the NgbDate month starts with 1
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    });
  }

  /**
   * Check if two date ranges overlap (for details of the implementation see https://stackoverflow.com/a/325964)
   *
   * @param range1 First date range
   * @param range2 Second date range
   */
  static overlaps(range1: DateRange, range2: DateRange) {
    const end1: number = +range1.to;
    const end2: number = +range2.to;

    const start1: number = +range1.from;
    const start2: number = +range2.from;

    return Math.max(start1, start2) <= Math.min(end1, end2);
  }

  /**
   * Returns a function that compares two given Dates
   * @param reverse If the sorting should be reversed
   */
  static sortByDate(reverse?: boolean) {
    const reversed = reverse ? 1 : -1;

    return function (a: { date: string }, b: { date: string }) {
      const date1 = moment(a.date);
      const date2 = moment(b.date);

      let returnValue;

      if (date1.isBefore(date2)) {
        returnValue = reversed;
      } else {
        returnValue = -1 * reversed;
      }

      return returnValue;
    };
  }

  static startOfDay(raw: RawDate) {
    return startOfDay(DateUtils.parse(raw));
  }

  static startOfToday() {
    return DateUtils.startOfDay(DateUtils.today());
  }

  static subDays(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return subDays(date, amount);
  }

  static subMonths(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return subMonths(date, amount);
  }

  static subQuarters(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return subQuarters(date, amount);
  }

  static subWeeks(raw: RawDate, amount: number) {
    const date = DateUtils.parse(raw);

    return subWeeks(date, amount);
  }

  static today(): Date {
    return new Date();
  }

  /**
   * Formats the given date to an iso time string
   * @param date
   */
  static toIso(date: RawDate): string {
    let returnValue;

    if (date !== undefined) {
      if (date === '23:59') {
        date = '23:59:59';
      }

      returnValue = DateUtils.format(date, 'HH:mm:ss', 'HH:mm:ss');
    }

    return returnValue;
  }

  /**
   * Input the number of milliseconds and get the string output such as '2h 35m 4s'. If the given
   * duration equals 0s, "-" is returned,
   */
  static formatAsDuration = (milliseconds: number): string => {
    const d = moment.duration(milliseconds);

    const h = d.hours();
    const m = d.minutes();
    const s = d.seconds();
    if (h > 0) {
      return h + 'h ' + m + 'm ' + s + 's';
    } else if (m > 0) {
      return m + 'm ' + s + 's';
    } else if (s > 0) {
      return Math.ceil(d.asSeconds()) + 's';
    } else {
      return '-';
    }
  };
}

export function datesAreEqual(raw1, raw2) {
  const date1 = moment(raw1);
  const date2 = moment(raw2);

  return date1.isSame(date2);
}

export function dateLikeToDate(raw: any) {
  const date = moment(raw);

  return date.toDate();
}

export function dateLikeToNgbDate(raw: any) {
  const date = moment(raw);

  return NgbDate.from({
    // 'date' returns the date of the month ('day' means the day of the week)
    day: date.get('date'),
    // Moments month is zero based whilst the NgbDate month starts with 1
    month: date.get('month') + 1,
    year: date.get('year'),
  });
}

export function differenceBetween(raw1: any, raw2: any, unit: any): number {
  const date1 = moment(raw1);
  const date2 = moment(raw2);

  return Math.abs(date1.diff(date2, unit));
}

export function formatDateForBackend(raw: any) {
  const date = moment(raw);

  return date.format(BACKEND_DATE_FORMAT);
}

export function sortByDate(reverse?: boolean) {
  const reversed = reverse ? 1 : -1;

  return function (a: { date: string }, b: { date: string }) {
    const date1 = moment(a.date);
    const date2 = moment(b.date);

    let returnValue;

    if (date1.isBefore(date2)) {
      returnValue = reversed;
    } else {
      returnValue = -1 * reversed;
    }

    return returnValue;
  };
}
